// Helpers for the BOM viewer. They work on the sections of GET outputs/{id}/bom:
// { name, isTree, columns: [{ label, role }], rows: [{ id, parentId, depth, values }] }
// Nothing here depends on the source; the comparison viewer uses them with rows that also carry a status.

// Key columns first, in the same order for every source; the rest keep the order of the file
const ROLE_ORDER = ["bubble", "item", "description", "quantity", "uom", "type", "revision", "lifecycle"];

// Shown by the tree itself
const HIDDEN_ROLES = ["level"];

export const SOURCE_STYLES = {
  Infor: "bom-source--infor",
  Database: "bom-source--database",
  Agile: "bom-source--agile",
  Oracle: "bom-source--oracle",
};

export const roleIndex = (section, role) => section.columns.findIndex((c) => c.role === role);

// Column indexes in display order
export const orderedColumns = (section) => {
  const indexes = section.columns.map((_, i) => i);
  const rank = (i) => {
    const r = ROLE_ORDER.indexOf(section.columns[i].role);
    return r < 0 ? ROLE_ORDER.length : r;
  };
  return indexes.sort((a, b) => rank(a) - rank(b) || a - b);
};

// Visible by default: every column with a value in some row, except the level
export const defaultVisibleColumns = (section) =>
  orderedColumns(section).filter(
    (i) => !HIDDEN_ROLES.includes(section.columns[i].role) && section.rows.some((r) => r.values[i])
  );

// Column that carries the tree indent: the bubble, else the item, else the first one
export const treeColumn = (section, visible) => {
  for (const role of ["bubble", "item"]) {
    const i = roleIndex(section, role);
    if (visible.includes(i)) return i;
  }
  return visible[0];
};

// Children of each row id, in file order; key "root" for the top rows
export const childrenMap = (rows) => {
  const map = new Map();
  for (const row of rows) {
    const key = row.parentId ?? "root";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return map;
};

const matches = (row, query, columns) =>
  !query || columns.some((i) => (row.values[i] || "").toLowerCase().includes(query));

// Rows to draw, with { row, hasChildren, expanded, context }.
// Tree: depth-first; while filtering, the parents of the matching rows are shown greyed out (context) and open.
// List: file order, only matching rows.
export const visibleRows = (section, { mode, query, expanded, filter, searchColumns }) => {
  const q = (query || "").trim().toLowerCase();
  const isMatch = (row) => matches(row, q, searchColumns) && (!filter || filter(row));
  const filtering = !!q || !!filter;

  if (!section.isTree || mode === "list") {
    return section.rows.filter(isMatch).map((row) => ({ row, hasChildren: false, expanded: false, context: false }));
  }

  const children = childrenMap(section.rows);
  const byId = new Map(section.rows.map((r) => [r.id, r]));
  let keep = null;
  if (filtering) {
    keep = new Set();
    for (const row of section.rows) {
      if (!isMatch(row)) continue;
      for (let r = row; r && !keep.has(r.id); r = byId.get(r.parentId)) keep.add(r.id);
    }
  }

  const out = [];
  const visit = (row) => {
    if (keep && !keep.has(row.id)) return;
    const kids = children.get(row.id) || [];
    const open = filtering || expanded.has(row.id);
    out.push({ row, hasChildren: kids.length > 0, expanded: open, context: filtering && !isMatch(row) });
    if (open) kids.forEach(visit);
  };
  (children.get("root") || []).forEach(visit);
  return out;
};

// Rows open by default: the first two levels
export const defaultExpanded = (section) =>
  new Set(section.rows.filter((r) => r.depth < 2).map((r) => r.id));

export const allParents = (section) => new Set(section.rows.filter((r) => r.parentId != null).map((r) => r.parentId));

// Ancestors of a row, top first
export const pathOf = (section, row) => {
  const byId = new Map(section.rows.map((r) => [r.id, r]));
  const path = [];
  for (let r = byId.get(row.parentId); r; r = byId.get(r.parentId)) path.unshift(r);
  return path;
};

// Short name of a row: bubble and item number, e.g. "UT01-CBNT · 980123"
export const rowName = (section, row) => {
  const parts = ["bubble", "item"].map((role) => row.values[roleIndex(section, role)]).filter(Boolean);
  return parts.join(" · ") || `Row ${row.id + 1}`;
};

// Count of rows per item type, most common first, for the type filter
export const typeCounts = (section) => {
  const type = roleIndex(section, "type");
  if (type < 0) return [];
  const counts = new Map();
  for (const row of section.rows) {
    const value = row.values[type];
    if (value) counts.set(value, (counts.get(value) || 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
};

export const distinctCount = (section, role) => {
  const i = roleIndex(section, role);
  return i < 0 ? 0 : new Set(section.rows.map((r) => r.values[i]).filter(Boolean)).size;
};

// Visible columns are remembered per source and tab, by label; null = use the default
const columnsKey = (source, section) => `bomViewer.columns.${source}.${section}`;

export const loadVisibleColumns = (source, section) => {
  try {
    const labels = JSON.parse(localStorage.getItem(columnsKey(source, section.name)) || "null");
    if (!Array.isArray(labels)) return null;
    const visible = orderedColumns(section).filter((i) => labels.includes(section.columns[i].label));
    return visible.length > 0 ? visible : null;
  } catch {
    return null;
  }
};

export const saveVisibleColumns = (source, section, visible) => {
  try {
    if (visible == null) localStorage.removeItem(columnsKey(source, section.name));
    else localStorage.setItem(columnsKey(source, section.name), JSON.stringify(visible.map((i) => section.columns[i].label)));
  } catch {
    // Storage blocked: the choice lasts until reload
  }
};

// Comparison viewer: row statuses, in the order of the filter chips
export const STATUSES = ["changed", "added", "deleted", "same"];

// sides = { a, b } with shortLabel: "Added in Dev", "Deleted in Dev", like the Comp Excel files
export const statusLabel = (status, sides) =>
  ({
    changed: "Changed",
    added: `Added in ${sides?.a.shortLabel ?? "A"}`,
    deleted: `Deleted in ${sides?.a.shortLabel ?? "A"}`,
    same: "No changes",
  })[status] || status;

// Value of column i in the second file (B) when it differs, undefined otherwise
export const beforeOf = (row, i) => row.changes?.find((c) => c.column === i)?.before;

// Columns with a difference in some row of a comparison
export const changedColumns = (section) => new Set(section.rows.flatMap((r) => (r.changes || []).map((c) => c.column)));

// "Changed: Quantity, Mfg_Cat", for the status marker
export const statusTitle = (section, row, sides) =>
  row.status === "changed" && row.changes?.length
    ? `Changed: ${row.changes.map((c) => section.columns[c.column]?.label).join(", ")}`
    : statusLabel(row.status, sides);

// Part Number the simulator could not resolve. Such a BOM must never be released to Agile, so the viewers flag it
// everywhere. Same rule as the "BOM Errors" tab of the Comp files.
const ISSUE_VALUES = ["NOT FOUND", "ERROR"];

export const partIssue = (value) => {
  const v = (value || "").trim().toUpperCase();
  return ISSUE_VALUES.includes(v) ? v : null;
};

export const partColumn = (section) => section.columns.findIndex((c) => c.label.trim().toLowerCase() === "part number");

// Part Number of a row in one file of a comparison: a = Dev, b = Prd; null when the row is not in that file
const partOn = (row, column, side) => {
  if (side === "a") return row.status === "deleted" ? null : row.values[column];
  if (row.status === "added") return null;
  if (row.status === "deleted") return row.values[column];
  return beforeOf(row, column) ?? row.values[column];
};

// True when the row has NOT FOUND or ERROR in either file
export const rowHasIssue = (section, row, column = partColumn(section)) =>
  column >= 0 && (!!partIssue(row.values[column]) || !!partIssue(beforeOf(row, column)));

// { notFound, error } in one tab; side a / b in a comparison, nothing for a single BOM
export const issueCounts = (section, side) => {
  const counts = { notFound: 0, error: 0 };
  const column = partColumn(section);
  if (column < 0) return counts;
  for (const row of section.rows) {
    const issue = partIssue(side ? partOn(row, column, side) : row.values[column]);
    if (issue === "NOT FOUND") counts.notFound++;
    if (issue === "ERROR") counts.error++;
  }
  return counts;
};

// "3 parts are NOT FOUND and 1 is ERROR"
export const describeIssues = ({ notFound, error }) => {
  const parts = [];
  if (notFound) parts.push(`${notFound} ${notFound === 1 ? "part is" : "parts are"} NOT FOUND`);
  if (error) parts.push(parts.length ? `${error} ${error === 1 ? "is" : "are"} ERROR` : `${error} ${error === 1 ? "part is" : "parts are"} ERROR`);
  return parts.join(" and ");
};

// Chip and badge text: "NOT FOUND", "ERROR" or both
export const issueLabel = ({ notFound, error }) =>
  notFound && error ? "NOT FOUND / ERROR" : error ? "ERROR" : "NOT FOUND";
